import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  computed,
  effect,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { KnowledgeSource, KnowledgeSourceConfigType } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../ai-knowledge.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { KnowledgeListComponent } from '../knowledge-list/knowledge-list.component';
import { CustomKnowledgeEditorComponent } from '../custom-knowledge-editor/custom-knowledge-editor.component';
import { LearnMoreBusinessProfileComponent } from '../learn-more-business-profile/learn-more-business-profile.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SHOW_BUSINESS_PROFILE_SOURCE_TOKEN } from '../tokens';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';

@Component({
  selector: 'ai-knowledge-application-knowledge',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    GalaxyLoadingSpinnerModule,
    GalaxyFormFieldModule,
    KnowledgeListComponent,
    MatDialogModule,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './application-knowledge.component.html',
  styleUrls: ['./application-knowledge.component.scss'],
})
export class ApplicationKnowledgeComponent implements OnInit {
  @Input() appId: string;
  @Input() disabled: boolean;

  @Output() selectedSourcesChange = new EventEmitter<KnowledgeSource[]>();

  DIALOG_WIDTH = '660px';
  private readonly dialog = inject(MatDialog);

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly showBusinessProfileSource = toSignal(inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN));
  sources = signal<KnowledgeSource[]>([]);
  searchValue = signal<string>('');
  selectedSources = signal<KnowledgeSource[]>(null);
  filteredSources = computed(() =>
    this.sources().filter(
      (source) =>
        source.name.toLowerCase().indexOf(this.searchValue()) >= 0 &&
        !this.selectedSources().some((selected) => selected.id === source.id),
    ),
  );
  readonly isLoading = signal(true);

  selectedSourceControl = new FormControl();

  @ViewChild('knowledgeSourceInput') knowledgeSourceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('trigger') matAutocompleteTrigger: MatAutocompleteTrigger;

  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => this.selectedSourcesChange.emit(this.selectedSources()));
  }

  async ngOnInit(): Promise<void> {
    await this.refreshSources();

    let initialSources: KnowledgeSource[] = [];
    if (this.appId) {
      initialSources = await this.knowledgeService.listAllKnowledgeSourcesForApp(this.appId);
    } else if (this.showBusinessProfileSource()) {
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource();
      if (businessProfileSource) {
        initialSources = [businessProfileSource];
      }
    }
    this.selectedSources.set(initialSources);
    this.isLoading.set(false);

    this.selectedSourceControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchValue) => {
      if (typeof searchValue === 'string') {
        this.searchValue.set(searchValue?.toLowerCase());
      }
    });
  }

  async refreshSources(): Promise<void> {
    // TODO: actually page and search on value changes
    const sourcesResult = await this.knowledgeService.listKnowledgeSources(
      [KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA],
      { pageSize: 100 },
    );
    const knowledgeSources = sourcesResult.knowledgeSources || [];
    if (this.showBusinessProfileSource()) {
      // manually insert business profile source because it may not exist yet
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource();
      if (businessProfileSource) {
        knowledgeSources.unshift(businessProfileSource);
      }
    }
    this.sources.set(knowledgeSources);
    this.clearInputFields();
  }

  async forceRefreshSources(): Promise<void> {
    this.isLoading.set(true);
    await this.refreshSources();
    this.isLoading.set(false);
  }

  async viewSource(source: KnowledgeSource): Promise<void> {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      this.dialog.open(LearnMoreBusinessProfileComponent, {
        width: this.DIALOG_WIDTH,
        data: { readOnly: true },
      });
      return;
    }
    this.dialog.open(CustomKnowledgeEditorComponent, {
      width: this.DIALOG_WIDTH,
      data: { knowledgeSource: source, readOnly: true },
    });
  }

  removeSource(source: KnowledgeSource): void {
    this.selectedSources.update((selectedSources) => {
      const index = selectedSources.indexOf(source);
      if (index >= 0) {
        selectedSources.splice(index, 1);
      }
      return [...selectedSources];
    });
  }

  addSource(source: KnowledgeSource): void {
    this.selectedSources.update((selectedSources) => {
      const sourceIds = selectedSources.map((g) => g.id);
      if (!sourceIds.includes(source.id)) {
        selectedSources.push(source);
      }
      this.clearInputFields();
      this.matAutocompleteTrigger.closePanel();
      return [...selectedSources];
    });
  }

  chooseMatch(event: any): void {
    event.preventDefault();
    if (this.filteredSources().length > 1) {
      // Do not select if there are multiple matches
      return;
    }
    if (this.filteredSources().length === 1) {
      const sourceToAdd = this.filteredSources()[0];
      this.addSource(sourceToAdd);
    }
  }

  clearInputFields(): void {
    if (this.knowledgeSourceInput?.nativeElement?.value) {
      this.knowledgeSourceInput.nativeElement.value = '';
    }
    this.selectedSourceControl.setValue('');
  }

  manageKnowledgeUrl(): string {
    return this.knowledgeService.buildManageKnowledgeUrl();
  }

  getKnowledgePreview(source: KnowledgeSource): string {
    return this.knowledgeService.getKnowledgePreview(source);
  }
}
